import { Link, useLocation, useParams } from 'react-router-dom';
import { useApi } from '../lib/useApi';
import { ExploreTabs } from '../components/ExploreTabs';
import { PublicHeader } from '../components/PublicHeader';
import { SiteFooter } from '../components/SiteFooter';
import { Card, ErrorBlock, LoadingBlock } from '../components/ui';
import { ReportListing } from '../components/ReportListing';
import '../styles/public.css';

/**
 * One provider's public profile (specification §7).
 *
 * Reached only when approved — the API answers 404 otherwise, because the
 * policy behind it admits nothing else. So there is no "pending" state to
 * render here and no status to check: a page that exists is a business that
 * passed review.
 */

interface Provider {
  id: string; slug: string; business_name: string; description: string | null;
  contact_name: string | null; phone: string; whatsapp: string | null;
  email: string | null; county: string; town: string | null;
  is_featured: boolean; category_slug: string; category_name: string;
}

const waLink = (n: string) => `https://wa.me/${n.replace(/[^0-9]/g, '')}`;

export function ServiceProvider() {
  const { slug } = useParams();
  const { pathname } = useLocation();
  const view = useApi<{ provider: Provider }>(slug ? `/api/v1/public/services/${slug}` : null);

  return (
    <>
      <PublicHeader />
      <main className="page public-page">
        <ExploreTabs path={pathname} />

        {view.loading ? <LoadingBlock />
          : view.error ? <ErrorBlock error={view.error} onRetry={view.reload} />
          : !view.data ? null
          : (() => {
            const p = view.data.provider;
            return (
              <>
                <div className="page-head">
                  <div>
                    <p className="muted" style={{ margin: 0 }}>
                      <Link to={`/services?category=${p.category_slug}`}>{p.category_name}</Link>
                    </p>
                    <h1 className="page-title">{p.business_name}</h1>
                    <p className="page-sub">
                      {p.town ? `${p.town}, ` : ''}{p.county}
                      {p.contact_name ? ` · ${p.contact_name}` : ''}
                    </p>
                  </div>
                </div>

                <div className="grid grid-2" style={{ alignItems: 'start' }}>
                  <Card title="About">
                    <p style={{ margin: 0 }}>
                      {p.description || 'This business has not added a description yet.'}
                    </p>
                  </Card>

                  {/* The number, in the open. §46 makes that a must-have, and a
                      directory that hides it behind a form is selling leads
                      rather than helping anyone find a plumber. */}
                  <Card title="Get in touch">
                    <p style={{ marginTop: 0 }}>
                      <a href={`tel:${p.phone}`} style={{ fontSize: 'var(--fs-h3, 20px)', fontWeight: 600 }}>
                        {p.phone}
                      </a>
                    </p>
                    <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
                      <a className="btn btn-primary" href={`tel:${p.phone}`}>Call now</a>
                      {p.whatsapp && (
                        <a className="btn btn-secondary" href={waLink(p.whatsapp)}
                          target="_blank" rel="noreferrer">WhatsApp</a>
                      )}
                      {p.email && (
                        <a className="btn btn-ghost" href={`mailto:${p.email}`}>Email</a>
                      )}
                    </div>
                    <p className="muted" style={{ marginBottom: 0, marginTop: 16 }}>
                      Listed on NyumbaSuite. Tell them where you found them.
                    </p>
                  </Card>
                </div>

                {/*
                  * Said plainly rather than implied by a badge.
                  *
                  * NyumbaSuite checks that a business is real before listing it;
                  * it does not inspect their work. A "Verified" badge would be
                  * read as the second, and reviews and verification are §46's
                  * Phase 2, not this one. Better to describe exactly what the
                  * check was than to let a visitor infer more than we did.
                  */}
                <p className="muted" style={{ marginTop: 24 }}>
                  NyumbaSuite reviews every business before it appears here. We do not
                  inspect work or guarantee it — agree the price and the job directly.
                </p>

                {/* §38. The check on the way in cannot catch a number that
                    stops being theirs, or a business that closes; the person
                    who rang it is the one who finds out. */}
                <ReportListing targetType="provider" targetId={p.id} what="business" />
              </>
            );
          })()}
      </main>
      <SiteFooter />
    </>
  );
}
